import { useEffect, useState, useCallback, useMemo } from "react";
import { useApp } from "@/context/AppContext";
import api, { apiError, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { Plus, Trash2, Loader2, Play, X, Newspaper, FileText, Radio, Film, Clapperboard, ExternalLink, Upload } from "lucide-react";

/* ---------- Flash breaking-news ticker ---------- */
export function FlashTicker() {
  const { t } = useApp();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/news?kind=flash").then(({ data }) => setItems(data)).catch(() => {}); }, []);
  if (items.length === 0) return null;
  const row = [...items, ...items];
  return (
    <div className="flex items-stretch bg-[#0B3B39] border-y border-amber-400/30 overflow-hidden" data-testid="flash-ticker">
      <div className="flex items-center gap-2 px-4 bg-red-600 text-white font-bold text-xs uppercase tracking-wider shrink-0">
        <Radio className="w-4 h-4 animate-pulse" /> {t.hub.breaking}
      </div>
      <div className="flex items-center whitespace-nowrap animate-ticker py-2.5">
        {row.map((n, i) => (
          <span key={`${n.id}-${i}`} className="inline-flex items-center gap-2 px-6 text-sm text-amber-50">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400" /> {n.title}
            {n.source && <span className="text-teal-300 text-xs">— {n.source}</span>}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ---------- Generic admin add-form modal ---------- */
function AddModal({ open, onClose, onSubmit, title, fields, hasFile, fileLabel = "Upload file", extraFile }) {
  const { t } = useApp();
  const [vals, setVals] = useState({});
  const [file, setFile] = useState(null);
  const [file2, setFile2] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await onSubmit(vals, file, file2); onClose(); setVals({}); setFile(null); setFile2(null); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center p-4 bg-black/60" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="oj-card p-7 w-full max-w-lg max-h-[88vh] overflow-y-auto" data-testid="add-modal">
        <div className="flex items-center justify-between mb-5"><h3 className="oj-h3">{title}</h3><button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button></div>
        <div className="space-y-4">
          {fields.map((f) => (
            f.type === "textarea" ? (
              <textarea key={f.name} rows={3} placeholder={f.label} value={vals[f.name] || ""} onChange={(e) => setVals({ ...vals, [f.name]: e.target.value })} className="oj-input resize-none" data-testid={`field-${f.name}`} required={f.required} />
            ) : f.type === "select" ? (
              <select key={f.name} value={vals[f.name] || f.options[0]} onChange={(e) => setVals({ ...vals, [f.name]: e.target.value })} className="oj-input" data-testid={`field-${f.name}`}>
                {f.options.map((o) => <option key={o} value={o} className="bg-white capitalize">{o}</option>)}
              </select>
            ) : (
              <input key={f.name} placeholder={f.label} value={vals[f.name] || ""} onChange={(e) => setVals({ ...vals, [f.name]: e.target.value })} className="oj-input" data-testid={`field-${f.name}`} required={f.required} />
            )
          ))}
          {hasFile && (
            <label className="flex items-center gap-3 oj-input cursor-pointer">
              <Upload className="w-5 h-5 text-teal-600 shrink-0" /><span className="text-sm text-slate-600 truncate">{file ? file.name : fileLabel}</span>
              <input type="file" accept={extraFile?.accept || "image/*,video/*,application/pdf"} className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="add-file" />
            </label>
          )}
          {extraFile && (
            <label className="flex items-center gap-3 oj-input cursor-pointer">
              <Film className="w-5 h-5 text-teal-600 shrink-0" /><span className="text-sm text-slate-600 truncate">{file2 ? file2.name : extraFile.label}</span>
              <input type="file" accept={extraFile.accept2 || "video/*"} className="hidden" onChange={(e) => setFile2(e.target.files[0])} data-testid="add-file2" />
            </label>
          )}
        </div>
        <button disabled={busy} className="oj-btn-gold w-full mt-6" data-testid="add-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {t.hub.publish}</button>
      </form>
    </div>
  );
}

/* ---------- e-Paper editions ---------- */
export function EPaperSection() {
  const { user, editMode, t } = useApp();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const canManage = user && editMode;
  const load = useCallback(() => api.get("/news?kind=epaper").then(({ data }) => setItems(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const add = async (v, file) => {
    const fd = new FormData(); fd.append("kind", "epaper"); fd.append("title", v.title || "Edition"); fd.append("source", v.source || ""); if (file) fd.append("file", file);
    await api.post("/news", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("e-Paper published"); load();
  };
  const del = async (id) => { await api.delete(`/news/${id}`); setItems((x) => x.filter((i) => i.id !== id)); toast.success("Removed"); };
  if (!canManage && items.length === 0) return null;
  return (
    <section className="oj-section bg-[#E9F5F4]/50 border-y border-teal-100">
      <div className="oj-container">
        <SectionHead icon={FileText} eyebrow={t.hub.printDigital} title={t.hub.epaperTitle} canManage={canManage} onAdd={() => setOpen(true)} />
        {items.length === 0 ? <Empty text="No e-paper editions yet." /> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
            {items.map((n, i) => (
              <Reveal key={n.id} delay={(i % 4) * 0.05}>
                <div className="group oj-card overflow-hidden" data-testid={`epaper-${n.id}`}>
                  <a href={n.media_path ? fileUrl(n.media_path) : (n.link || "#")} target="_blank" rel="noreferrer" className="block relative h-64 bg-teal-950/5">
                    {n.media_path && n.content_type?.startsWith("image") ? <img src={fileUrl(n.media_path)} alt={n.title} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-teal-600"><FileText className="w-12 h-12" /></div>}
                    <span className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#014845] to-transparent p-3 text-white text-sm font-semibold">{n.title}</span>
                  </a>
                  {canManage && <button onClick={() => del(n.id)} data-testid={`epaper-del-${n.id}`} className="absolute top-2 right-2 grid place-items-center w-8 h-8 rounded-lg bg-red-600 text-white"><Trash2 className="w-4 h-4" /></button>}
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </div>
      <AddModal open={open} onClose={() => setOpen(false)} onSubmit={add} title="Publish e-Paper Edition" hasFile fileLabel="Upload PDF or image page" fields={[{ name: "title", label: "Edition title (e.g. 25 Sep 2026)", required: true }, { name: "source", label: "Publication / source" }]} />
    </section>
  );
}

/* ---------- Press-bureau video/news feed ---------- */
export function BureauFeed() {
  const { user, editMode, t } = useApp();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const canManage = user && editMode;
  const load = useCallback(() => api.get("/news?kind=bureau").then(({ data }) => setItems(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const add = async (v, file) => {
    const fd = new FormData(); fd.append("kind", "bureau"); fd.append("title", v.title); fd.append("summary", v.summary || ""); fd.append("source", v.source || ""); fd.append("link", v.link || ""); if (file) fd.append("file", file);
    await api.post("/news", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Update published"); load();
  };
  const del = async (id) => { await api.delete(`/news/${id}`); setItems((x) => x.filter((i) => i.id !== id)); toast.success("Removed"); };
  if (!canManage && items.length === 0) return null;
  return (
    <section className="oj-section">
      <div className="oj-container">
        <SectionHead icon={Newspaper} eyebrow={t.hub.bureau} title={t.hub.bureauTitle} canManage={canManage} onAdd={() => setOpen(true)} />
        {items.length === 0 ? <Empty text="No press updates yet." /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((n, i) => {
              const isVideo = n.content_type?.startsWith("video");
              return (
                <Reveal key={n.id} delay={(i % 3) * 0.06}>
                  <div className="oj-card overflow-hidden h-full flex flex-col" data-testid={`bureau-${n.id}`}>
                    {n.media_path && isVideo ? (
                      <video src={fileUrl(n.media_path)} className="w-full h-48 object-cover bg-black" controls preload="metadata" />
                    ) : n.media_path ? (
                      <img src={fileUrl(n.media_path)} alt={n.title} className="w-full h-48 object-cover" />
                    ) : (
                      <div className="w-full h-48 grid place-items-center bg-[#E9F5F4] text-teal-600"><Newspaper className="w-10 h-10" /></div>
                    )}
                    <div className="p-5 flex-1 flex flex-col">
                      <div className="flex items-center justify-between gap-2">
                        {n.source && <span className="text-[10px] font-mono uppercase tracking-wider text-teal-700 bg-teal-50 border border-teal-200 rounded px-2 py-0.5">{n.source}</span>}
                        {canManage && <button onClick={() => del(n.id)} data-testid={`bureau-del-${n.id}`} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>}
                      </div>
                      <h4 className="font-bold text-[#0B3B39] mt-2 leading-snug">{n.title}</h4>
                      {n.summary && <p className="text-sm text-slate-600 mt-1.5 leading-relaxed flex-1">{n.summary}</p>}
                      {n.link && <a href={n.link} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-teal-600 hover:text-amber-600">{t.hub.readSource} <ExternalLink className="w-3.5 h-3.5" /></a>}
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        )}
      </div>
      <AddModal open={open} onClose={() => setOpen(false)} onSubmit={add} title="Add Press Bureau Update" hasFile fileLabel="Upload video/image (optional)" fields={[{ name: "title", label: "Headline", required: true }, { name: "summary", label: "Summary", type: "textarea" }, { name: "source", label: "Source (e.g. PIB, PTI)" }, { name: "link", label: "External link (optional)" }]} />
    </section>
  );
}

/* ---------- OTT: web series & shorts ---------- */
export function OttSection() {
  const { user, editMode, t } = useApp();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(false);
  const [player, setPlayer] = useState(null);
  const canManage = user && editMode;
  const load = useCallback(() => api.get("/webseries").then(({ data }) => setItems(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const add = async (v, poster, video) => {
    const fd = new FormData();
    fd.append("title", v.title); fd.append("description", v.description || ""); fd.append("kind", v.kind || "series"); fd.append("genre", v.genre || ""); fd.append("year", v.year || ""); fd.append("video_url", v.video_url || "");
    if (poster) fd.append("poster", poster); if (video) fd.append("video", video);
    await api.post("/webseries", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Published to OTT"); load();
  };
  const del = async (id) => { await api.delete(`/webseries/${id}`); setItems((x) => x.filter((i) => i.id !== id)); toast.success("Removed"); };
  const filtered = useMemo(() => (tab === "all" ? items : items.filter((i) => i.kind === tab)), [items, tab]);
  const tabLabels = { all: t.hub.all, series: t.hub.series, short: t.hub.shorts };
  if (!canManage && items.length === 0) return null;
  return (
    <section className="oj-section bg-[#0B3B39]" data-testid="ott-section">
      <div className="oj-container">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="oj-eyebrow mb-3 !text-amber-300"><Clapperboard className="w-4 h-4" /> {t.hub.ott}</div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white">{t.hub.ottTitle}</h2>
          </div>
          <div className="flex items-center gap-2">
            {["all", "series", "short"].map((k) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`ott-tab-${k}`} className={`px-4 py-2 rounded-full text-sm font-semibold capitalize transition-all ${tab === k ? "bg-amber-400 text-[#014845]" : "bg-white/10 text-white border border-white/20 hover:bg-white/20"}`}>{tabLabels[k]}</button>
            ))}
            {canManage && <button onClick={() => setOpen(true)} className="oj-btn-gold !py-2 !px-4 !text-sm" data-testid="ott-add"><Plus className="w-4 h-4" /> {t.hub.upload}</button>}
          </div>
        </div>
        {filtered.length === 0 ? <div className="text-center py-14 text-teal-200/70">{t.hub.noTitles}</div> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
            {filtered.map((w, i) => (
              <Reveal key={w.id} delay={(i % 5) * 0.05}>
                <div className="group relative" data-testid={`ott-item-${w.id}`}>
                  <button onClick={() => setPlayer(w)} className="block w-full text-left">
                    <div className="relative aspect-[2/3] rounded-xl overflow-hidden bg-teal-900 border border-white/10 shadow-lg">
                      {w.poster_path ? <img src={fileUrl(w.poster_path)} alt={w.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <div className="w-full h-full grid place-items-center text-teal-500"><Film className="w-10 h-10" /></div>}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                      <div className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"><div className="grid place-items-center w-12 h-12 rounded-full bg-amber-400 text-[#014845]"><Play className="w-5 h-5 ml-0.5" fill="currentColor" /></div></div>
                      <span className="absolute top-2 left-2 px-2 py-0.5 rounded text-[9px] font-mono uppercase bg-black/60 text-amber-300 border border-amber-400/40">{w.kind === "short" ? t.hub.short : t.hub.series}</span>
                    </div>
                    <h4 className="mt-2 text-sm font-semibold text-white truncate">{w.title}</h4>
                    <p className="text-xs text-teal-200/60 truncate">{[w.genre, w.year].filter(Boolean).join(" • ")}</p>
                  </button>
                  {canManage && <button onClick={() => del(w.id)} data-testid={`ott-del-${w.id}`} className="absolute top-2 right-2 grid place-items-center w-8 h-8 rounded-lg bg-red-600 text-white z-10"><Trash2 className="w-4 h-4" /></button>}
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </div>
      <AddModal open={open} onClose={() => setOpen(false)} onSubmit={add} title="Upload Web Series / Short" hasFile fileLabel="Poster image" extraFile={{ label: "Video file (optional)", accept: "image/*", accept2: "video/*" }}
        fields={[{ name: "title", label: "Title", required: true }, { name: "kind", label: "Type", type: "select", options: ["series", "short"] }, { name: "genre", label: "Genre" }, { name: "year", label: "Year" }, { name: "description", label: "Description", type: "textarea" }, { name: "video_url", label: "Video URL (YouTube/embed, optional)" }]} />
      {player && <PlayerModal item={player} onClose={() => setPlayer(null)} />}
    </section>
  );
}

function PlayerModal({ item, onClose }) {
  const embed = item.video_url ? toEmbed(item.video_url) : null;
  return (
    <div className="fixed inset-0 z-[80] grid place-items-center p-4 bg-black/85" onClick={onClose} data-testid="ott-player">
      <div className="w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3"><h3 className="text-lg font-bold text-white">{item.title}</h3><button onClick={onClose} className="text-white/70 hover:text-white"><X className="w-6 h-6" /></button></div>
        <div className="aspect-video rounded-xl overflow-hidden bg-black">
          {item.video_path ? <video src={fileUrl(item.video_path)} className="w-full h-full" controls autoPlay /> :
            embed ? <iframe src={embed} title={item.title} className="w-full h-full" allow="autoplay; encrypted-media; fullscreen" allowFullScreen /> :
            <div className="w-full h-full grid place-items-center text-white/60">No video source provided.</div>}
        </div>
        {item.description && <p className="text-sm text-teal-100/80 mt-3">{item.description}</p>}
      </div>
    </div>
  );
}

function toEmbed(url) {
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|live\/))([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  return url;
}

function SectionHead({ icon: Icon, eyebrow, title, canManage, onAdd }) {
  const { t } = useApp();
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
      <div><div className="oj-eyebrow mb-3"><Icon className="w-4 h-4" /> {eyebrow}</div><h2 className="oj-h2">{title}</h2></div>
      {canManage && <button onClick={onAdd} className="oj-btn-gold !py-2.5 !px-5 !text-sm" data-testid={`add-${title.replace(/\W+/g, "-").toLowerCase()}`}><Plus className="w-4 h-4" /> {t.hub.add}</button>}
    </div>
  );
}
function Empty({ text }) { return <div className="oj-card p-10 text-center text-slate-500">{text}</div>; }
